<template>
  <div>
    <div class="relative">
      <h4 style="color: #30384b !important" class="font-semibold my-2 md:my-0 mb-2">
        Tasks
      </h4>
    </div>
    <vx-card class="flex mt-4 p-4 pt-1 overflow-hidden w-full">
      <vs-row vs-lg="12" class="my-4 flex items-start" v-for="(i, index) in boardSteps" :key="index">
        <vs-col class="flex relative justify-center" vs-lg="1" vs-sm="1" vs-xs="1">
          <div class="icon relative items-center z-10" :class="index === 0 || i.reviewStatus === 'USER_ACCEPTED'
            ? 'active-icon'
            : 'inactive-icon'
            ">
            <!-- tick icon  -->
            <vs-avatar v-if="index === 0 || i.reviewStatus === 'USER_ACCEPTED'" color="#44CC88" icon="done" />
            <vs-avatar v-else color="#f2c244" size="30px" class="font-semibold" :text="`${index + 1}`" />
          </div>
          <div v-if="index < boardSteps.length - 1" class="absolute vertical-line" :class="[
            index === 0 || i.reviewStatus === 'USER_ACCEPTED'
              ? 'active-border'
              : 'inactive-border',
          ]" />
        </vs-col>

        <vs-col class="mt-0 ml-2 md:ml-9 md:mt-0 md:mb-0 md:mr-0" vs-w="10">
          <div class="text-left">
            
            <!-- TITLE AND ICON ROW -->
            <div class="flex items-center flex-wrap gap-3 mb-1">
              <p class="cursor-pointer font-semibold text-lg mb-0" style="color: #383b46 !important">
                <b> {{ i.title }} </b>
              </p>
              
              <!-- Inline App Trigger Icon (User Side) -->
              <vx-tooltip 
                v-if="i.colData && i.colData.type" 
                :text="getStepActionTitle(i.colData)" 
                position="top"
              >
                <div @click.stop="getStepAction(i)"
                     class="flex items-center justify-center p-1.5 bg-slate-50 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-100 transition-colors shadow-sm ml-2">
                  
                  <!-- App Icons -->
                  <img v-if="i.colData.type === 'apps' && getAppIcon(i.colData)" :src="getAppIcon(i.colData)" class="w-8 h-8 object-contain" alt="App" />
                  
                  <!-- Social Icons -->
                  <feather-icon v-else-if="i.colData.type === 'socialConnections' && i.colData.socialStepData === 'linkedin'" icon="LinkedinIcon" class="w-4 h-4 text-[#0077b5]" />
                  <feather-icon v-else-if="i.colData.type === 'socialConnections' && i.colData.socialStepData === 'discord'" icon="MessageSquareIcon" class="w-4 h-4 text-[#5865F2]" />
                  <feather-icon v-else-if="i.colData.type === 'socialConnections' && i.colData.socialStepData === 'twitter'" icon="TwitterIcon" class="w-4 h-4 text-[#1DA1F2]" />
                  <feather-icon v-else-if="i.colData.type === 'socialConnections' && i.colData.socialStepData === 'facebook'" icon="FacebookIcon" class="w-4 h-4 text-[#1877F2]" />
                  <feather-icon v-else-if="i.colData.type === 'socialConnections'" icon="Share2Icon" class="w-4 h-4 text-gray-600" />
                  
                  <!-- Custom Link / Attachment -->
                  <feather-icon v-else-if="i.colData.type === 'customLink' && i.colData.attachmentName" icon="PaperclipIcon" class="w-4 h-4 text-primary" />
                  <feather-icon v-else-if="i.colData.type === 'customLink'" icon="LinkIcon" class="w-4 h-4 text-primary" />
                  
                  <!-- Fallback -->
                  <feather-icon v-else icon="ExternalLinkIcon" class="w-4 h-4 text-gray-500" />
                  
                </div>
              </vx-tooltip>
            </div>
            
            <div>
              <!-- Rich text rendering -->
              <span class="activity-desc text-base html-content block mt-1" v-if="i.description" v-html="i.description"></span>

              <!-- Prevent empty container rendering if colData is missing -->
              <div v-if="pendingStepIndex === index"
                class="p-4 border border-slate-200 rounded-lg mt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-slate-50 transition-all">
                
                <div class="flex items-center">
                  <vs-checkbox 
                    :value="i.reviewStatus === 'USER_ACCEPTED'" 
                    @input="handleCheckboxToggle($event, i)"
                  >
                    <span class="font-semibold text-slate-700 ml-2 text-base">Mark this as complete</span>
                  </vs-checkbox>
                </div>
              </div>
            </div>
          </div>
        </vs-col>
      </vs-row>
    </vx-card>

    <!-- for joining casts/rooms  -->
    <vs-popup :title="popupTitle" :active.sync="isEventsPopup">
      <div class="flex flex-col gap-3" v-if="eventsLoading">
        <div class="bg-grey-500 p-10 rounded-lg animate-pulse" v-for="i in 5" :key="i"></div>
      </div>

      <div v-else>
        <div v-if="currentEvents.length > 0">
          <vs-card class="w-full mb-0" v-for="(event, i) in this.currentEvents" :key="i">
            <vs-col>
              <h4 class="flex gap-3 font-semibold text-lg">
                {{ event.event_name }}
                <div class="flex items-center gap-1 text-red-600" v-if="event.is_running === 'false'">
                  <span class="h-3 w-3 rounded-full bg-red-600 opacity-75"></span>
                  offline
                </div>

                <div v-else class="flex items-center gap-2 text-green-600">
                  <span class="animate-ping h-2 w-2 rounded-full bg-green-500 opacity-75"></span>
                  Live
                </div>
              </h4>
            </vs-col>
            <div class="flex items-center justify-between mt-2 w-full">
              <h6 class="text-slate-400">
                <span>{{ event.event_date }}</span>
                | <span>{{ formatTimestamp(event.event_time) }}</span>
              </h6>
              <vs-button class="rounded-lg p-2 text-black" @click.native="joinEvents(event)">Go to {{ popupTitle ===
                "Casts" ? "cast" : "room" }}</vs-button>
            </div>
          </vs-card>
        </div>

        <div v-else class="flex flex-col items-center justify-center">
          <img src="@/assets/images/empty-events.svg" alt="Empty" class="w-3/4" />
          <h4 class="normal-case">
            No <span class="lowercase">{{ popupTitle }}</span> are available at
            the moment.
          </h4>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import axios from "axios";
import marketplaceList from "@/assets/data/marketplace-list.js";
import appFlow from "@/assets/data/app-flow.js";
import AppStep from "./AppStep.vue";
import { connectSocialSteps, web3Steps } from "@/assets/data/journeySteps.js";
import videowikiCastMixin from "../mixins/videowikiCastMixin";
import constants from "@/constants.js"

export default {
  name: "user-board-step",
  props: {
    userLevelBoardAnalytics: {
      type: Object,
    },
  },
  mixins: [videowikiCastMixin],
  components: { AppStep },
  data() {
    return {
      constants,
      appFlow,
      connectSocialSteps,
      web3Steps,
      marketplaceList,
      showBoardTasksTip: false,
      orgSlug: "",
      boardSteps: [],
      isEventsPopup: false,
      pendingStepIndex: 1,
      orgId: "",
      boardName: "",

      // Mapped Coach Action Steps and specific target URLs
      coachSteps: [
        { id: 'set-a-goal', title: 'Set a goal', url: 'https://coach.getboarded.com/user/add-skill' },
        { id: 'meet-your-mentor', title: 'Meet your mentor', url: 'https://coach.getboarded.com/user/my-coach/profile/view' },
        { id: 'invite-advisors', title: 'Invite Advisors', url: 'https://coach.getboarded.com/user/my-rater/rater-list' },
        { id: 'motivation-surveys', title: 'Motivation Survey', url: 'https://coach.getboarded.com/user/coaching-experience' }
      ]
    };
  },
  computed: {
    boardId() {
      return this.$route.params.boardId;
    },
    userData() {
      const ud = this.$store.state.users.data;
      if (ud.email) return ud;
      else return this.$store.state.auth.user;
    },
    blinkers() {
      return this.$store.state.dashboard.blinkers;
    },
  },
  watch: {
    userData() {
      this.fetchUserBoardProgress();
    },
    boardId: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.fetchUserBoardProgress();
        this.getBoardSteps();
      },
    },
  },
  methods: {
    getOrgDetails() {
      this.$store
        .dispatch("boards/fetchBoardDetails", this.boardId)
        .then((res) => {
          this.orgId = res.data.orgData._id;
          this.orgColor = res.data.orgData.primaryThemeColor;
          this.orgSlug = res.data.orgData.slug;
          this.boardName = res.data.title;
        })
        .catch((err) => {
          console.log("err", err);
        });
    },
    fetchUserBoardProgress() {
      if (this.userData && this.userData._id && this.boardId) {
        this.$store.dispatch("boards/fetchUserBoardProgress", {
          boardId: this.boardId,
          userId: this.userData._id,
        });
      }
    },
    
    getAppIcon(colData) {
      if (!colData || colData.type !== 'apps' || !colData.appSelected) return null;
      // Safe fallback if local DB has 'mentor' but marketplace only has 'coach' registered
      let app = this.marketplaceList.find(a => a.id === colData.appSelected);
      if (!app && (colData.appSelected === 'mentor' || colData.appSelected === 'coach')) {
         app = this.marketplaceList.find(a => a.id === 'coach' || a.id === 'mentor');
      }
      return app && app.icon ? app.icon : null;
    },

    getAppActionTitle(colData) {
      if (!colData || colData.type !== 'apps' || !colData.appSelected || !colData.appStepData) return null;
      
      if (colData.appSelected === 'mentor' || colData.appSelected === 'coach') {
        const step = this.coachSteps.find(s => s.id === colData.appStepData);
        return step ? step.title : "Action";
      }

      // [NEW FIX]: Ensure "Offers" is correctly labeled
      if (colData.appSelected === 'resume-editor' && colData.appStepData === 'offers') {
        return "Offers";
      }

      if (!this.appFlow.flow[colData.appSelected]) return "Open App";
      
      const step = this.appFlow.flow[colData.appSelected].steps.find(s => s.id === colData.appStepData);
      return step ? step.title : "Action";
    },
    
    readFilterDown() {
      this.showBoardTasksTip = false;
      this.$store.commit("dashboard/UPDATE_DASHBOARD_BLINKERS", {
        status: true,
        key: "readBoardTasksTip",
      });
    },
    handleClickOutside(event) {
      const popup = this.$refs.blinkerTip;
      const wrapper = this.$refs.wrapper;

      if (!popup.contains(event.target) && !wrapper.contains(event.target)) {
        this.showBoardTasksTip = false;
        window.removeEventListener("click", this.handleClickOutside);
      }
    },
    showBlinkerTip() {
      this.showBoardTasksTip = true;
      window.addEventListener("click", this.handleClickOutside);
    },

    getBoardSteps() {
      this.$store
        .dispatch("boards/fetchBoardSteps", {
          boardId: this.boardId,
          userId: this.userData._id,
        })
        .then((stepsRes) => {
          if (!stepsRes || !stepsRes.data) return;
          let steps = stepsRes.data;

          this.$store
            .dispatch("kanban/getKanbanDataV2", this.boardId)
            .then((kanbanRes) => {
              if (kanbanRes.data && kanbanRes.data.status === "SUCCESS") {
                const cols = kanbanRes.data.data.cols;
                let actualUserColIndex = 0;

                for (let i = 0; i < cols.length; i++) {
                  const userInCol = cols[i].colUsersData.find(u => String(u.userId) === String(this.userData._id));
                  if (userInCol) {
                    actualUserColIndex = i;
                    break;
                  }
                }

                this.pendingStepIndex = actualUserColIndex;
                
                this.boardSteps = steps.map((step, index) => {
                  if (index < actualUserColIndex) {
                    step.reviewStatus = "USER_ACCEPTED";
                  } else {
                    step.reviewStatus = "PENDING";
                  }

                  // Inject Kanban Col Data so the App Icon populates
                  if (cols[index] && cols[index].colData) {
                    step.colData = cols[index].colData;
                  }

                  return step;
                });
              } else {
                this.boardSteps = steps; 
              }
            });
        })
        .catch((err) => {
          console.error("Error fetching steps:", err);
        });
    },

    getStepActionTitle(colData) {
      if (!colData || !colData.type) return "Action";
      
      // 1. Apps (Coach, Mentor, Resume Editor)
      if (colData.type === 'apps' && colData.appSelected && colData.appStepData) {
        if (colData.appSelected === 'mentor' || colData.appSelected === 'coach') {
          const step = this.coachSteps.find(s => s.id === colData.appStepData);
          return step ? step.title : "Coach Action";
        }
        
        // [NEW FIX]: Return proper title for Offers
        if (colData.appSelected === 'resume-editor' && colData.appStepData === 'offers') {
          return "Offers";
        }

        if (this.appFlow.flow[colData.appSelected]) {
          const step = this.appFlow.flow[colData.appSelected].steps.find(s => s.id === colData.appStepData);
          return step ? step.title : "App Action";
        }
        return "App Action";
      }
      
      // 2. Custom Links & Attachments
      if (colData.type === 'customLink') {
        return colData.attachmentName ? `Download: ${colData.attachmentName}` : "Open Link";
      }
      
      // 3. Social Connections (Replaces "Connect" with "Add")
      if (colData.type === 'socialConnections') {
        const platform = colData.socialStepData;

        switch (platform) {
          case "linkedin": return "Add LinkedIn";
          case "facebook": return "Add Facebook";
          case "discord":  return "Add Discord";
          case "twitter":  return "Add Twitter";
          default:         return "Add Social Account";
        }
      }
      
      return "Action";
    },
    
    openSocialSso() {
      const accessToken = localStorage.getItem("accessToken") || this.$store.state.bearerToken;
      const org = this.orgSlug || 'default';
      const uId = this.userData._id;
      
      // FIX: Send just the plain word "contact". 
      // The Editor app's router will automatically add the slash and domain!
      const editorSsoUrl = `https://editor.getboarded.com/oauth-callback?organization=${org}&token=${accessToken}&userId=${uId}&redirectTo=contact`;
      
      window.open(editorSsoUrl, "_blank");
    },
    
    // SSO SYNC & ACTION METHOD
    async openCoachActionPortal(targetUrl) {
      this.$vs.loading();
      const coachApi = "https://api.coach.getboarded.com"; 
      const pythonApiUrl = process.env.VUE_APP_API_URL || "https://api.getboarded.tech";
      
      try {
        const roleRes = await axios.get(`${pythonApiUrl}/api/v2/organization/${this.orgId}/user/${this.userData._id}/is-coach`);
        
        let currentRole = "trainee"; 
        if (roleRes.data && roleRes.data.is_coach) {
          currentRole = "coach";
        }

        const checkRes = await axios.get(`${coachApi}/users?email=${this.userData.email}`);
        let currentMysqlId = null;

        if (checkRes.data && checkRes.data.total === 0) {
          const newUserPayload = {
            email: this.userData.email,
            firstName: this.userData.firstName || "User", 
            lastName: this.userData.lastName || "",      
            username: this.userData.email.split("@")[0],
            userType: currentRole,                        
            password: Math.random().toString(36).slice(-8) + "A1!", 
            status: "active",
            flag: "sso" 
          };
          const createRes = await axios.post(`${coachApi}/users`, newUserPayload);
          currentMysqlId = createRes.data.id;
          
        } else if (checkRes.data && checkRes.data.total > 0) {
          const existingUser = checkRes.data.data[0];
          currentMysqlId = existingUser.id;
          
          if (currentRole === 'coach' && existingUser.userType !== 'coach') {
            await axios.patch(`${coachApi}/users/${currentMysqlId}`, {
              userType: 'coach',
              flag: 'sso'
            });
          }
        }

        const linkRes = await this.$store.dispatch("auth/generateMagicLink", {
          email: this.userData.email,
          loginCode: "1234", 
          redirectTo: targetUrl || "https://coach.getboarded.com", 
        });
        
        if (linkRes && linkRes.data && linkRes.data.invitePhrase) {
          let url = `${coachApi}/oauth-auto?invitePhrase=${encodeURIComponent(linkRes.data.invitePhrase)}`;
          window.open(url, "_blank");
        }
        
      } catch (err) {
        console.error("Coach Access Error:", err);
        this.$vs.notify({
          title: 'Access Denied',
          text: 'Could not connect to the Coach portal at this time.',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    getStepAction(step) {
      if (step.colData) {
        let stepData = {};
        const { type, appSelected, appStepData } = step.colData;

        // 1. Custom Links / Attachments
        if (type === "customLink") {
          window.open(step.colData.customLink, "_blank");
          return;
        }

        // 2. Social Connections (REDIRECT TO EDITOR)
        if (type === "socialConnections") {
          this.openSocialSso();
          return;
        }
        
        // 3. Web3
        if (type === "web3") {
          stepData = this.web3Steps.find(
            (web3Step) => web3Step.id === step.colData.web3StepData
          );
        } 
        
        // 4. Apps & Coach
        if (type === "apps") {
          // Divert flow to our mapped SSO Links for Coach Application
          if (appSelected === 'mentor' || appSelected === 'coach') {
             const stepInfo = this.coachSteps.find(s => s.id === appStepData);
             if (stepInfo && stepInfo.url) {
                this.openCoachActionPortal(stepInfo.url);
                return;
             }
          }

          // [NEW FIX]: Explicitly route to Offers inside the Editor App
          if (appSelected === 'resume-editor' && appStepData === 'offers') {
            const accessToken = localStorage.getItem("accessToken") || this.$store.state.bearerToken;
            const org = this.orgSlug || 'default';
            const uId = this.userData._id;
            window.open(`https://editor.getboarded.com/oauth-callback?organization=${org}&token=${accessToken}&userId=${uId}&redirectTo=offers`, "_blank");
            return;
          }

          // Legacy flows for other apps
          if (this.appFlow.flow[appSelected]) {
            stepData = this.appFlow.flow[appSelected].steps.find(
              (appStep) => appStep.id === appStepData
            );
          }
        }

        if (stepData && stepData.id === "joincast") {
          return this.getAllCasts(this.boardId);
        }

        if (stepData && stepData.id === "joinRoom") {
          return this.getAllRooms(this.boardId);
        }

        if (stepData && stepData.url) {
          this.routeToUrl(stepData.url);
        }
      }
    },

    routeToUrl(url) {
      if (!url) return;
      let accessToken = localStorage.getItem("accessToken");
      
      // Added null-safety fallback values to prevent replacing issues
      let finalRoutingUrl = url
        .replace("${token}", accessToken || '')
        .replace("${orgId}", this.orgId || '')
        .replace("${orgSlug}", this.orgSlug || '')
        .replace("${firstName}", (this.userData && this.userData.firstName) ? this.userData.firstName : '')
        .replace("${userEmail}", (this.userData && this.userData.email) ? this.userData.email : '')
        .replace("${userId}", (this.userData && this.userData._id) ? this.userData._id : '')
        .replace("${boardId}", this.boardId || '');
        
      window.open(finalRoutingUrl, "_blank");
    },
    joinEvents(event) {
      let { attendee_url, room_url, creator_email } = event;
      if (room_url) {
        return this.routeToUrl(room_url);
      }
      if (creator_email === this.userData.email) {
        this.$store
          .dispatch("videowiki/joinCastByCreator", {
            publicMeetingId: event.public_meeting_id,
            passkey: event.h_mp,
          })
          .then((res) => {
            window.open(res.cast_url, "_blank");
          })
          .catch((err) => {
            throw err;
          });
      } else {
        this.routeToUrl(attendee_url);
      }
      this.isEventsPopup = false;
    },
    handleCheckboxToggle(isChecked, step) {
      if (!isChecked) return;
      
      this.$vs.loading();
      
      const pythonApiUrl = process.env.VUE_APP_API_URL;
      
      axios.post(`${pythonApiUrl}/api/v2/boards/${this.boardId}/users/${this.userData._id}/advance-step`)
        .then((res) => {
          if (res.data.status === "success") {
            this.$vs.notify({
              title: 'Success',
              text: 'Task complete! Moving to next stage.',
              color: 'success',
              iconPack: 'feather',
              icon: 'icon-check-circle'
            });
            
            this.getBoardSteps(); 
          }
        })
        .catch((err) => {
          console.error("Failed to advance step:", err);
          this.$vs.notify({
            title: 'Error',
            text: 'Could not complete task.',
            color: 'danger'
          });
        })
        .finally(() => {
          this.$vs.loading.close();
        });
    },

  },
  mounted() {
    this.getBoardSteps();
  },
  created() {
    this.fetchUserBoardProgress();
    this.getOrgDetails();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vuexy/components/vxTimeline.scss";

.blinker {
  left: 3.5em;
  top: 0.75em;

  &-color {
    background: rgba(var(--vs-primary, "#F2C244"), 1) !important;
  }

  &-details {
    background: #fae0a0;
    left: 3em;
    top: 2.5em;
    width: 25em;
    border-radius: 8px;
    z-index: 10;
  }
}
</style>

<style scoped>
::v-deep .html-content a {
  color: #1976d2 !important;
  text-decoration: underline !important;
  cursor: pointer;
}
::v-deep .html-content strong {
  font-weight: bold !important;
}
::v-deep .html-content em {
  font-style: italic !important;
}
::v-deep .html-content u {
  text-decoration: underline !important;
}
::v-deep .html-content p {
  margin-bottom: 0.25rem !important;
  display: inline-block;
}

.vx-card>>>.vx-card__body {
  padding: 0;
}

.vx-card>>>.vx-card__collapsible-content {
  width: 100%;
}
</style>